import { useState } from "react";
import { useBackNav, goBack } from "../lib/backnav.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const TEXT = "#cfe0ff", MUTED = "#8a93a6", AMBER = "#e0a458", GREEN = "#6fcf97",
  RED = "#e07070", ACCENT = "#6699ff", BORDER = "rgba(255,255,255,0.08)",
  CARD = "rgba(255,255,255,0.03)";
const card = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: "12px 14px" } as const;
const btn = { fontSize: 12, padding: "5px 12px", borderRadius: 5, cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.14)", background: "transparent",
  color: TEXT } as const;
const backBtn = { ...btn, fontWeight: 700, color: "#cfe0ff",
  border: "1px solid #6699ff88", background: "rgba(102,153,255,0.18)",
  padding: "7px 16px" } as const;

async function j<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    ...init,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as any).detail ?? `Failed (${res.status})`);
  return body as T;
}

const sevColor = (s: string) => (s === "critical" ? RED : s === "warning" ? AMBER : MUTED);

export function ReviewTab({ onOpenResults }: {
  onOpenResults?: (rfp: string) => void }) {
  const [rfp, setRfp] = useState<string | null>(null);
  const [pid, setPid] = useState<string | null>(null);
  useBackNav([
    { active: rfp != null, close: () => setRfp(null) },
    { active: pid != null, close: () => setPid(null) },
  ]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {!rfp && <Queue open={setRfp} />}
      {rfp && !pid && <Proposals rfp={rfp} back={goBack} open={setPid}
        onOpenResults={onOpenResults} />}
      {rfp && pid && <Detail rfp={rfp} pid={pid} back={goBack} />}
    </div>
  );
}

function Queue({ open }: { open: (r: string) => void }) {
  const q = useQuery({ queryKey: ["hitl", "rfps"], queryFn: () => j<any>("/api/v1/hitl/rfps") });
  if (q.isLoading) return <div style={{ color: MUTED, padding: 24 }}>Loading queue…</div>;
  if (q.isError) return <div style={{ color: RED, padding: 24 }}>{String((q.error as Error).message)}</div>;
  const rfps = q.data?.rfps ?? [];
  return (
    <div className="panel">
      <div className="panel-head"><h2 style={{ margin: 0 }}>Review queue</h2></div>
      {rfps.length === 0 && <div style={{ color: MUTED, padding: 20, fontSize: 13 }}>No RFPs with visible proposals.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rfps.map((r: any) => (
          <div key={r.rfp_id} style={{ ...card, cursor: "pointer" }} onClick={() => open(r.rfp_id)}>
            <span style={{ color: ACCENT, fontWeight: 600, fontSize: 14 }}>{r.rfp_id}</span>
            <span style={{ color: MUTED, fontSize: 12, marginLeft: 14 }}>
              {r.proposal_count} proposal{r.proposal_count === 1 ? "" : "s"} ·{" "}
              {Object.entries(r.state_counts).map(([s, n]) => `${s}: ${n}`).join(" · ") || "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Proposals({ rfp, back, open, onOpenResults }: { rfp: string; back: () => void;
  open: (p: string) => void; onOpenResults?: (r: string) => void }) {
  const q = useQuery({ queryKey: ["hitl", "proposals", rfp],
    queryFn: () => j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/proposals`) });
  return (
    <div className="panel">
      <div className="panel-head" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <button style={backBtn} onClick={back}>← Queue</button>
        <h2 style={{ margin: 0 }}>{rfp}</h2>
      </div>
      {q.isLoading && <div style={{ color: MUTED, padding: 20 }}>Loading…</div>}
      {q.isError && <div style={{ color: RED, padding: 20 }}>{String((q.error as Error).message)}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(q.data?.proposals ?? []).map((p: any) => (
          <div key={p.proposal_id} style={{ ...card, cursor: "pointer" }} onClick={() => open(p.proposal_id)}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: TEXT, fontWeight: 600 }}>{p.entity_name ?? p.proposal_id}</span>
              <span style={{ color: MUTED, fontSize: 12 }}>v{p.latest_version} · {p.state ?? "—"}</span>
            </div>
            <div style={{ fontSize: 12, color: MUTED, display: "flex", gap: 12 }}>
              {(["critical", "warning", "info"] as const).map((s) =>
                p.auto_flag_counts?.[s] ? (
                  <span key={s} style={{ color: sevColor(s) }}>{p.auto_flag_counts[s]} {s}</span>
                ) : null)}
              <span>{p.review_count} recommendation{p.review_count === 1 ? "" : "s"}</span>
              {p.finalized && <span style={{ color: GREEN }}>FINALIZED</span>}
            </div>
          </div>
        ))}
      </div>
      <ScoringCard rfp={rfp} isOperator={q.data?.reviewer_role === "operator"}
        onOpenResults={onOpenResults} />
    </div>
  );
}

function ScoringCard({ rfp, isOperator, onOpenResults }: { rfp: string;
  isOperator: boolean; onOpenResults?: (r: string) => void }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const q = useQuery({ queryKey: ["hitl", "scoring", rfp],
    queryFn: () => j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/scoring`) });
  const d = q.data;
  const run = () => {
    if (busy) return;
    if (d?.scored && !window.confirm(
      "Re-scoring OVERWRITES the existing evaluation artifacts for this RFP. Continue?")) return;
    setBusy(true); setMsg(null);
    j<any>(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/score`, { method: "POST" })
      .then((r) => { setMsg(`Scored ${r.proposals_scored} proposal(s)`);
        qc.invalidateQueries({ queryKey: ["hitl"] }); })
      .catch((e: Error) => setMsg(e.message))
      .finally(() => setBusy(false));
  };
  const ranked = (d?.evaluation?.evaluation_output ?? d?.evaluation ?? {}).ranked_list ?? [];
  return (
    <div style={{ ...card, marginTop: 14 }}>
      <div style={{ fontSize: 11, color: MUTED, marginBottom: 8,
        letterSpacing: 0.6 }}>PROPOSAL SCORING</div>
      {q.isLoading && <div style={{ fontSize: 12, color: MUTED }}>Checking…</div>}
      {d && (
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {d.scored ? (
            <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>
              SCORED{d.scored_at ? ` · ${d.scored_at}` : ""}</span>
          ) : d.ready ? (
            <span style={{ fontSize: 12, color: GREEN }}>
              READY · {d.approved_count} approved proposal(s)</span>
          ) : (
            <span style={{ fontSize: 12, color: AMBER }}>
              NOT READY · {typeof d.blocking === "string" ? d.blocking :
                "all proposals must be finalized first"}</span>
          )}
          {isOperator && (d.ready || d.scored) && (
            <button onClick={run} disabled={busy}
              style={{ ...btn, fontWeight: 700, color: "#0a0e17", border: "none",
                background: busy ? MUTED : ACCENT }}>
              {busy ? "Scoring…" : d.scored ? "Re-Score" : "Run scoring"}</button>
          )}
          {d.scored && (
            <button style={btn} onClick={() => onOpenResults ? onOpenResults(rfp) :
              window.open(`/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/scoring/report`, "_blank")}>
              View Results</button>
          )}
        </div>
      )}
      {ranked.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {ranked.map((e: any, i: number) => (
            <div key={i} style={{ fontSize: 12, color: TEXT, padding: "3px 0" }}>
              #{e.rank ?? i + 1} · <b>{e.entity_name ?? e.proposal_id}</b>
              {e.composite_score != null ? ` · ${Number(e.composite_score).toFixed(2)}` : ""}
              {e.hard_fail ? <span style={{ color: RED }}> · HARD FAIL</span> : null}
            </div>
          ))}
        </div>
      )}
      {msg && <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>{msg}</div>}
    </div>
  );
}

function Detail({ rfp, pid, back }: { rfp: string; pid: string; back: () => void }) {
  const qc = useQueryClient();
  const base = `/api/v1/hitl/rfp/${encodeURIComponent(rfp)}/proposal/${encodeURIComponent(pid)}`;
  const q = useQuery({ queryKey: ["hitl", "detail", rfp, pid], queryFn: () => j<any>(base) });
  const [decision, setDecision] = useState("APPROVE");
  const [rationale, setRationale] = useState("");
  const [flagMsg, setFlagMsg] = useState("");
  const [flagPath, setFlagPath] = useState("");
  const [flagSev, setFlagSev] = useState("info");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["hitl"] });
  const act = (p: Promise<any>, ok: string) => {
    setMsg(null); setErr(null);
    p.then((r) => { setMsg(ok + (r.conflict_detected ? " — CONFLICT detected (split decisions)" : "")); refresh(); })
     .catch((e: Error) => setErr(e.message));
  };

  if (q.isLoading) return <div style={{ color: MUTED, padding: 24 }}>Loading proposal…</div>;
  if (q.isError) return <div style={{ color: RED, padding: 24 }}>{String((q.error as Error).message)}</div>;
  const d = q.data;
  const isOperator = d.reviewer?.role === "operator";
  const inConflict = d.finalization == null &&
    new Set(Object.values(d.recommendations ?? {}).map((r: any) => r.decision)).size > 1;
  // Approvals are one-click; adverse decisions require recorded reasoning.
  const rationaleOk = decision === "APPROVE" || rationale.trim().length >= 20;

  return (
    <div className="panel">
      <div className="panel-head" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <button style={backBtn} onClick={back}>← Proposals</button>
        <h2 style={{ margin: 0 }}>{d.proposal?.entity?.name ?? pid}</h2>
        <span style={{ color: MUTED, fontSize: 12 }}>v{d.version} · {rfp}</span>
        {d.finalization && <span style={{ color: GREEN, fontSize: 12, fontWeight: 700 }}>
          FINALIZED: {d.finalization.decision}</span>}
        {inConflict && <span style={{ color: RED, fontSize: 12, fontWeight: 700 }}>CONFLICT</span>}
      </div>

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: MUTED, marginBottom: 8 }}>FLAGS</div>
        {[...(d.auto_flags ?? []).map((f: any) => ({ ...f, auto: true })),
          ...(d.manual_flags ?? [])].map((f: any, i: number) => (
          <div key={f.flag_id ?? i} style={{ display: "flex", gap: 10, alignItems: "center",
            fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ color: sevColor(f.severity), fontWeight: 700, minWidth: 64 }}>
              {(f.severity ?? "info").toUpperCase()}</span>
            <span style={{ color: TEXT, flex: 1 }}>{f.message}
              {f.field_path ? (
                <span style={{ color: MUTED }}> · {f.field_path}</span>
              ) : null}</span>
            <span style={{ color: MUTED }}>{f.auto ? "auto" : f.reviewer_id}</span>
            {!f.auto && f.flag_id && (
              <button style={btn} onClick={() =>
                act(j(`${base}/flag/${f.flag_id}?version=${d.version}`, { method: "DELETE" }), "Flag removed")}>
                remove</button>)}
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <select value={flagSev} onChange={(e) => setFlagSev(e.target.value)}
            style={{ ...btn, cursor: "pointer" }}>
            {["info", "warning", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={flagMsg} onChange={(e) => setFlagMsg(e.target.value)}
            placeholder="flag message" style={{ ...btn, flex: 1, cursor: "text" }} />
          <button style={btn} disabled={!flagMsg.trim()} onClick={() =>
            act(j(`${base}/flag`, { method: "POST", body: JSON.stringify({
              version: d.version, severity: flagSev, message: flagMsg,
              field_path: "" }) }), "Flag added")}>
            + Flag</button>
        </div>
      </div>

      <ProposalDoc proposal={d.proposal} onFlag={setFlagPath} />
      {flagPath && (
        <FlagModal path={flagPath} onClose={() => setFlagPath("")}
          onSubmit={(sev, msg) => {
            act(j(`${base}/flag`, { method: "POST", body: JSON.stringify({
              version: d.version, severity: sev, message: msg,
              field_path: flagPath }) }), `Flag added · ${flagPath}`);
            setFlagPath("");
          }} />
      )}

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: MUTED, marginBottom: 8 }}>RECOMMENDATIONS</div>
        {Object.entries(d.recommendations ?? {}).map(([rid, r]: [string, any]) => (
          <div key={rid} style={{ fontSize: 12, color: TEXT, padding: "4px 0" }}>
            <b style={{ color: r.decision === "APPROVE" ? GREEN : r.decision === "REJECT" ? RED : AMBER }}>
              {r.decision}</b> — {r.display_name}: {r.rationale}
          </div>
        ))}
        {!d.finalization && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              {["APPROVE", "RETURN", "REJECT"].map((dec) => (
                <button key={dec} onClick={() => setDecision(dec)}
                  style={{ ...btn, border: `1px solid ${decision === dec ? ACCENT : "rgba(255,255,255,0.14)"}`,
                    background: decision === dec ? "rgba(102,153,255,0.16)" : "transparent" }}>
                  {dec}</button>
              ))}
            </div>
            <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={2}
              placeholder={decision === "APPROVE"
                ? "Rationale (optional for approval — recorded in the audit trail)"
                : "Rationale (minimum 20 characters — recorded in the audit trail)"}
              style={{ width: "100%", boxSizing: "border-box", fontSize: 12, color: TEXT, padding: 8,
                background: "rgba(255,255,255,0.05)", borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.14)" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button style={{ ...btn, background: rationaleOk ? ACCENT : MUTED, color: "#0a0e17",
                fontWeight: 700, border: "none" }} disabled={!rationaleOk}
                onClick={() => act(j(`${base}/recommend`, { method: "POST", body: JSON.stringify({
                  version: d.version, decision, rationale }) }), `Recommended ${decision}`)}>
                Recommend</button>
              {isOperator && (
                <button style={{ ...btn, background: rationaleOk ? GREEN : MUTED, color: "#0a0e17",
                  fontWeight: 700, border: "none" }} disabled={!rationaleOk}
                  onClick={() => act(j(`${base}/finalize`, { method: "POST", body: JSON.stringify({
                    version: d.version, decision, rationale,
                    current_state: inConflict ? "CONFLICT" : "HITL_REVIEW" }) }),
                    `Finalized ${decision}`)}>
                  {inConflict ? "Resolve conflict" : "Finalize"} (operator)</button>
              )}
            </div>
          </div>
        )}
        {msg && <div style={{ color: GREEN, fontSize: 12, marginTop: 8 }}>{msg}</div>}
        {err && <div style={{ color: RED, fontSize: 12, marginTop: 8 }}>{err}</div>}
      </div>

    </div>
  );
}

const SKIP_KEYS = new Set(["proposal_id", "state", "schema_version", "rfp_id",
  "mandate_id", "submitted_at", "token",
  // operator/internal sections — not MM submission data
  "internal", "commitment_classifications", "extraction", "completeness"]);
const HIDDEN_FIELDS = new Set(["source_format", "source_hash",
  "extraction_method", "extraction_confidence"]);
const label = (k: string) =>
  k.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
const scalar = (v: any) =>
  v == null || v === "" ? "-"
  : typeof v === "number" ? v.toLocaleString()
  : typeof v === "boolean" ? (v ? "Yes" : "No")
  : String(v);

function ProposalDoc({ proposal, onFlag }: { proposal: any;
  onFlag: (k: string) => void }) {
  if (!proposal) return null;
  const sections = Object.entries(proposal).filter(
    ([k, v]) => !SKIP_KEYS.has(k) && v != null && typeof v === "object");
  return (
    <>
      {sections.map(([name, body]) => (
        <div key={name} style={{ ...card, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: MUTED, letterSpacing: 0.6 }}>
              {label(name).toUpperCase()}</span>
            <button style={{ ...btn, fontSize: 10, padding: "2px 8px" }}
              onClick={() => onFlag(name)}>⚑ flag section</button>
          </div>
          <Section body={body} path={name} onFlag={onFlag} />
        </div>
      ))}
    </>
  );
}

function Section({ body, path, onFlag }: { body: any; path: string;
  onFlag: (k: string) => void }) {
  if (Array.isArray(body)) {
    if (body.length === 0) return <div style={{ fontSize: 12, color: MUTED }}>-</div>;
    if (typeof body[0] === "object" && body[0] != null) {
      const cols = Array.from(new Set(body.flatMap((r: any) => Object.keys(r))));
      return (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{cols.map((c) => (
              <th key={c} style={{ textAlign: "left", padding: "5px 8px", fontSize: 10,
                color: MUTED, textTransform: "uppercase",
                borderBottom: `1px solid ${BORDER}` }}>{label(c)}</th>))}</tr></thead>
            <tbody>{body.map((r: any, i: number) => (
              <tr key={i}>{cols.map((c) => (
                <td key={c} style={{ padding: "6px 8px", fontSize: 12, color: TEXT,
                  borderBottom: `1px solid ${BORDER}` }}>{scalar(r[c])}</td>))}</tr>
            ))}</tbody>
          </table>
        </div>
      );
    }
    return <div style={{ fontSize: 12, color: TEXT }}>{body.map(scalar).join(", ")}</div>;
  }
  const entries = Object.entries(body).filter(([k]) => !HIDDEN_FIELDS.has(k));
  // dict-of-dicts (e.g. per-venue commitments): titled sub-blocks, not rows
  const allObjects = entries.length > 1 && entries.every(
    ([, v]) => v != null && typeof v === "object" && !Array.isArray(v));
  if (allObjects) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {entries.map(([k, v]) => (
          <div key={k} style={{ border: `1px solid ${BORDER}`, borderRadius: 6,
            padding: "8px 10px" }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>
                {label(k)}</span>
              <button style={{ ...btn, fontSize: 9, padding: "1px 6px", color: MUTED }}
                onClick={() => onFlag(`${path}.${k}`)}>⚑</button>
            </div>
            <Section body={v} path={`${path}.${k}`} onFlag={onFlag} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: "flex", gap: 12, padding: "4px 0",
          borderBottom: `1px solid ${BORDER}`, fontSize: 12 }}>
          <span style={{ color: MUTED, width: "var(--m-label-w, 240px)", flexShrink: 0, overflowWrap: "break-word" }}>
            {label(k)}{"\u00a0"}
            <button style={{ ...btn, fontSize: 9, padding: "0 5px", color: MUTED,
              border: "none" }} title={`flag ${path}.${k}`}
              onClick={() => onFlag(`${path}.${k}`)}>⚑</button>
          </span>
          {v != null && typeof v === "object"
            ? <div style={{ flex: 1 }}><Section body={v} path={`${path}.${k}`} onFlag={onFlag} /></div>
            : <span style={{ color: TEXT, whiteSpace: "pre-wrap" }}>{scalar(v)}</span>}
        </div>
      ))}
    </div>
  );
}


function FlagModal({ path, onClose, onSubmit }: { path: string;
  onClose: () => void; onSubmit: (sev: string, msg: string) => void }) {
  const [sev, setSev] = useState("info");
  const [msg, setMsg] = useState("");
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60,
      background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center",
      justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(460px, calc(100vw - 32px))",
        background: "#11151f", border: `1px solid ${BORDER}`, borderRadius: 10,
        padding: "20px 22px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 4 }}>
          Add flag</div>
        <div style={{ fontSize: 12, color: ACCENT, marginBottom: 14,
          wordBreak: "break-all" }}>{path}</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {["info", "warning", "critical"].map((s) => (
            <button key={s} onClick={() => setSev(s)}
              style={{ ...btn, color: sev === s ? sevColor(s) : MUTED,
                border: `1px solid ${sev === s ? sevColor(s) : "rgba(255,255,255,0.14)"}` }}>
              {s.toUpperCase()}</button>
          ))}
        </div>
        <textarea autoFocus value={msg} onChange={(e) => setMsg(e.target.value)}
          rows={3} placeholder="What's the issue with this field?"
          style={{ width: "100%", boxSizing: "border-box", fontSize: 13, color: TEXT,
            padding: 10, background: "rgba(255,255,255,0.05)", borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.14)" }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8,
          marginTop: 12 }}>
          <button style={btn} onClick={onClose}>Cancel</button>
          <button disabled={!msg.trim()}
            style={{ ...btn, fontWeight: 700, color: "#0a0e17", border: "none",
              background: msg.trim() ? ACCENT : MUTED }}
            onClick={() => onSubmit(sev, msg)}>Add flag</button>
        </div>
      </div>
    </div>
  );
}
